import { useCallback } from 'react';

import { Stack, Typography } from '@mui/material';

import { ActionsMenuItem } from '@/components/ActionsMenu';
import MapPinIcon from '@/components/Icons/MapPinIcon';
import { MAX_PINNED_IDS } from '@/constants';
import useUser from '@/hooks/useUser';
import { POLL_INTERVAL } from '@/sections/Workspace/hooks';
import { useRecoilState } from 'recoil';

import { pinnedIdsState } from '../atoms';
import { WorkspaceSections } from '../types';
import { sortPinnedIds } from '../utils';
import { useTrainingJobsQuery } from './__generated__/queries.generated';

function useTrainingJobs(poll = false) {
  const { data, loading } = useTrainingJobsQuery({
    fetchPolicy: 'cache-first',
    pollInterval: poll ? POLL_INTERVAL : null,
  });

  return {
    trainingJobs: data?.trainingJobs || [],
    loading,
  };
}

function usePinnedAPI() {
  const user = useUser();
  const [pinnedIds, setPinnedIds] = useRecoilState(
    pinnedIdsState(`${WorkspaceSections.Training}-${user.id}`),
  );
  const { trainingJobs, loading } = useTrainingJobs();

  const pinnedTrainingJobs = trainingJobs
    .filter((trainingJob) => pinnedIds.includes(trainingJob.id))
    .sort((a, b) => sortPinnedIds(pinnedIds, a.id, b.id));

  const togglePinEntity = useCallback(
    (entityId: string) => {
      setPinnedIds((prev) =>
        prev.includes(entityId) ? prev.filter((id) => id !== entityId) : [entityId, ...prev],
      );
    },
    [setPinnedIds],
  );

  const canPinMoreTrainingJobs = pinnedTrainingJobs.length < MAX_PINNED_IDS;

  function getPinActionItem(entityId: string) {
    const isPinned = pinnedIds.includes(entityId);
    const isPinDisabled = !canPinMoreTrainingJobs && !isPinned;

    return (
      <ActionsMenuItem
        onClick={() => togglePinEntity(entityId)}
        data-cy="pin-entity-btn"
        icon={<MapPinIcon color="secondary" />}
        disabled={isPinDisabled}
      >
        <Stack direction="row" flex={1} alignItems="center" justifyContent="space-between">
          {isPinned ? 'Unpin' : 'Pin'}
          {isPinDisabled && (
            <Typography variant="body2" color="text.secondary">
              Limit {MAX_PINNED_IDS}
            </Typography>
          )}
        </Stack>
      </ActionsMenuItem>
    );
  }

  return {
    pinnedIds,
    togglePinEntity,
    canPinMoreTrainingJobs,
    getPinActionItem,
    pinnedTrainingJobs,
    loading,
  };
}

export { useTrainingJobs, usePinnedAPI };
